import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';

import { rialToToman } from '@/utilities/Financial';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { getProductBuyHref } from '@/utilities/getProductBuyHref';
import { withSeparator } from '@/utilities/withSeparator';

import { batteryHeights, batteryPoles, isSuggestedBattery } from '@/constants/batteryProduct';

import { ProductType } from '@/server/services/core/product/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { ArrowLeft, MagicStar } from 'iconsax-react';

interface CampaignProductCardProps {
    product: ProductType & Partial<ProductBatteryType>;
}

const CampaignProductCard = ({ product }: CampaignProductCardProps) => {
    const detailsHref = `/product/${product.uuid}/${product.slug}`;
    const buyHref = getProductBuyHref(product);

    const hasDiscount = !!(product.discountPercentage && product.sellingPrice);
    const finalPrice = hasDiscount ? product.sellingPrice : product.price;

    const specs = [
        product.ampere ? `${product.ampere} آمپر` : '',
        product.pole ? batteryPoles[product.pole] : '',
        product.height ? batteryHeights[product.height] : ''
    ].filter(Boolean);

    return (
        <article
            className='group relative flex w-[268px] shrink-0 snap-start flex-col overflow-hidden rounded-lg bg-white/[0.05] ring-1 ring-white/10
                transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.08] hover:ring-primary/70 sm:w-auto'>
            {isSuggestedBattery(product) && (
                <span
                    className='absolute left-3 top-3 z-10 flex items-center gap-1 rounded-full bg-primary/90 px-2 py-1 text-[10px] font-bold
                        text-white'>
                    <MagicStar size={12} color='currentcolor' />
                    پیشنهاد ازکی‌سرویس
                </span>
            )}

            <Link href={detailsHref} className='flex flex-col'>
                <div className='relative flex h-40 items-center justify-center overflow-hidden'>
                    <span aria-hidden className='absolute h-28 w-28 rounded-full bg-primary/25 blur-2xl' />
                    <SafeImage
                        width={130}
                        height={130}
                        src={
                            GenerateFilePreviewUrl({
                                attachableId: product.uuid,
                                attachment: 'PRODUCT',
                                type: 'MAIN',
                                width: 300,
                                height: 300,
                                isPrimary: true,
                                sortOrder: 0
                            }).src
                        }
                        alt={product.title}
                        fallback='/images/noImage/noImage.png'
                        className='relative object-contain transition-transform duration-300 group-hover:scale-105'
                    />
                    {hasDiscount && (
                        <span className='absolute right-3 top-3 rounded-full bg-amber-400 px-2 py-1 text-[11px] font-extrabold text-[#0A1530]'>
                            {product.discountPercentage}٪ تخفیف
                        </span>
                    )}
                </div>

                <div className='flex flex-col gap-1 px-4'>
                    <Typography variant='subtitle2' component='h3' fontWeight={700} className='line-clamp-1 text-white'>
                        {product.title}
                    </Typography>
                    <Typography variant='caption' component='p' className='line-clamp-1 text-whiteMediumEmphasis'>
                        {product.brand.name}
                        {product.guarantee ? ` • گارانتی ${product.guarantee} ماهه` : ''}
                    </Typography>
                    {!!specs.length && (
                        <div className='mt-1 flex flex-wrap gap-1'>
                            {specs.map((spec) => (
                                <span key={spec} className='rounded-lg bg-white/10 px-1.5 py-0.5 text-[10px] text-white/70'>
                                    {spec}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </Link>

            <div className='mt-auto flex items-end justify-between gap-2 p-4 pt-3'>
                {product.available ? (
                    <>
                        <div className='flex flex-col gap-0.5'>
                            {hasDiscount && (
                                <Typography variant='overline' component='p' className='text-white/40 line-through'>
                                    {withSeparator(rialToToman(product.price))}
                                </Typography>
                            )}
                            <Typography variant='h6' component='span' fontWeight={800} className='text-amber-300'>
                                {withSeparator(rialToToman(finalPrice))}
                                <span className='mr-1 text-[10px] font-normal text-white/50'>تومان</span>
                            </Typography>
                        </div>
                        <Link href={buyHref}>
                            <Button size='small' variant='contained' endIcon={<ArrowLeft size={16} color='currentcolor' />}>
                                خرید
                            </Button>
                        </Link>
                    </>
                ) : (
                    <Typography variant='body2' component='p' fontWeight={600} className='text-white/40'>
                        ناموجود
                    </Typography>
                )}
            </div>
        </article>
    );
};

export default CampaignProductCard;
